/**
 * The recorder — the half of the Mission Recorder that "operates silently and
 * patiently in the background while you storm the skies" (manual, THE MISSION
 * RECORDER).
 *
 * It is always on. The original asked "Would you like to record this mission?"
 * *before* the flight because a 1990 machine had to decide in advance whether
 * it could afford the disk traffic; this port asks "Would you like to save this
 * mission to a tape?" only *after*, because there is nothing to decide. The
 * cost of recording is one array push per rendered frame in the common case and
 * one encounter hash every five seconds, which is why the answer can be left
 * until the pilot has seen how the sortie went.
 *
 * ## The two things it is fed, and why they are enough
 *
 * `flew(inputs, ticks)` — a rendered frame simulated `ticks` sim ticks, all of
 * them with `inputs`. That is not an approximation of what happened; it is
 * literally `sim-runner.ts`'s contract (`runTicks` reads the stick once per
 * frame), so the recorder reconstructs the per-tick stick exactly while only
 * being told about it once per frame.
 *
 * `observe(state)` — the encounter at the end of a frame, for the checkpoint
 * schedule. `encounterHash` is far too expensive to take every tick
 * (docs/engineering/lessons.md, 2026-08-20: a 10,000-tick duel hashed per tick
 * took 8.5 s), so it is taken on a fixed interval and the recorder keeps the
 * *actual* tick each hash belongs to rather than assuming a regular grid — a
 * frame can straddle a boundary, and a checkpoint whose tick is a guess is a
 * checkpoint that will fail to compare.
 *
 * ## Full trace, not a ring buffer
 *
 * The brief left this to measurement, and measurement says a full trace: a
 * quarter-hour sortie's trace is tens of kilobytes, because the trace grows
 * with *changes of stick* rather than with ticks (`input-trace.ts`). A ring
 * buffer would cost the same to run, lose the beginning of every flight, and
 * make seeking to the start impossible. `replay.md` carries the measured
 * numbers.
 */

import { encounterHash } from "../combat/encounter-hash.ts";
import type { EncounterState } from "../combat/encounter.ts";
import type { FlightInputs } from "../flight/inputs.ts";
import { appendRun, traceTicks, type InputRun } from "./input-trace.ts";
import {
  currentGuard,
  subjectHash,
  tapeName,
  TAPE_FORMAT,
  TAPE_FORMAT_VERSION,
  type ReplayTape,
  type TapeCheckpoint,
  type TapeSubject,
} from "./replay-tape.ts";

/**
 * Ticks between encounter hashes — five seconds at 60 Hz.
 *
 * Chosen against the two costs it sits between. Hashing is ~0.4 ms for a
 * crowded encounter, so five-second spacing is well under a tenth of a percent
 * of a frame budget; and a divergence never heals, so a replay that has gone
 * wrong is proved wrong within five seconds of the tick that broke it. A
 * regression fixture that wants tighter spacing passes its own interval.
 */
export const CHECKPOINT_TICKS = 300;

export interface RecorderOptions {
  readonly subject: TapeSubject;
  /** Which aircraft id the trace is the stick for. */
  readonly playerId: string;
  /** What the tape list should call the flight — the mission's own title. */
  readonly title: string;
  readonly checkpointTicks?: number;
}

/**
 * Collects a flight as it is flown. Cheap enough to leave running for every
 * sortie; `seal` is what turns what it has collected into a tape.
 */
export class ReplayRecorder {
  private readonly runs: InputRun[] = [];
  private readonly marks: TapeCheckpoint[] = [];
  private readonly interval: number;
  private nextCheckpoint: number;

  constructor(private readonly options: RecorderOptions) {
    this.interval = options.checkpointTicks ?? CHECKPOINT_TICKS;
    this.nextCheckpoint = this.interval;
  }

  /** Ticks recorded so far — the Location Counter's source. */
  get ticks(): number {
    return traceTicks(this.runs);
  }

  /** How many checkpoints have been taken. A tape with none has nothing to verify against. */
  get checkpoints(): number {
    return this.marks.length;
  }

  /**
   * One rendered frame's worth of flying: `ticks` sim ticks, every one of them
   * flown with `inputs`. `inputs` must already be `quantizeInputs`'d — the same
   * value the sim stepped on — or the tape stores a stick that was never flown.
   */
  flew(inputs: FlightInputs, ticks: number): void {
    appendRun(this.runs, ticks, inputs);
  }

  /** The encounter at a frame boundary. Hashes it only when the schedule is due. */
  observe(state: EncounterState): void {
    if (state.tick < this.nextCheckpoint) return;
    this.marks.push([state.tick, encounterHash(state)]);
    this.nextCheckpoint = state.tick + this.interval;
  }

  /**
   * Seals what has been recorded into a tape. `name` is trimmed to the
   * original's eight characters; `recordedAt` is injected rather than read off
   * a clock so a fixture recorded by a tool is byte-for-byte reproducible.
   */
  seal(name: string, recordedAt: string): ReplayTape {
    return {
      format: TAPE_FORMAT,
      version: TAPE_FORMAT_VERSION,
      name: tapeName(name),
      title: this.options.title,
      recordedAt,
      guard: currentGuard(),
      playerId: this.options.playerId,
      ticks: this.ticks,
      subject: this.options.subject,
      subjectHash: subjectHash(this.options.subject),
      trace: [...this.runs],
      checkpoints: [...this.marks],
    };
  }
}
